import clsx from 'clsx';
import type { CSSProperties, FunctionComponent, HTMLAttributes, ReactNode } from 'react';

import { recessedSurfaceClassNames } from '../surface/surfaceClasses';

export type RecessedSurfacePlacement = 'inside' | 'outside';
export type RecessedSurfacePocketTone = 'neutral' | 'orange';
export type RecessedSurfaceRadius = 'default' | 'md';
export type RecessedSurfaceRenderMode = 'layout' | 'overlay';
export type RecessedSurfaceShadow = 'none' | 'orange-sm' | 'sm';
export type RecessedSurfaceTone = 'gray' | 'inherit' | 'orange' | 'white';
export type RecessedSurfaceTransition = 'default' | 'none';

const pocketClassNameByTone: Record<RecessedSurfacePocketTone, string> = {
    neutral: recessedSurfaceClassNames.pocket.neutral,
    orange: recessedSurfaceClassNames.pocket.orange,
};

const hoverPocketClassNameByTone: Record<RecessedSurfacePocketTone, string> = {
    neutral: 'hover:surface-recessed-pocket-neutral',
    orange: 'hover:surface-recessed-pocket-orange',
};

const overlayPocketClassNameByTone: Record<RecessedSurfacePocketTone, string> = {
    neutral: 'surface-recessed-overlay-pocket-neutral',
    orange: 'surface-recessed-overlay-pocket-orange',
};

const hoverOverlayPocketClassNameByTone: Record<RecessedSurfacePocketTone, string> = {
    neutral: 'hover:surface-recessed-overlay-pocket-neutral focus-within:surface-recessed-overlay-pocket-neutral',
    orange: 'hover:surface-recessed-overlay-pocket-orange focus-within:surface-recessed-overlay-pocket-orange',
};

const surfaceClassNameByTone: Record<RecessedSurfaceTone, string> = {
    gray: recessedSurfaceClassNames.inner.gray,
    inherit: '',
    orange: recessedSurfaceClassNames.inner.orange,
    white: 'bg-white-200 text-gray-700',
};

const radiusClassNamesByRadius: Record<RecessedSurfaceRadius, { readonly inner: string; readonly pocket: string }> = {
    default: {
        inner: recessedSurfaceClassNames.radius.inner,
        pocket: recessedSurfaceClassNames.radius.pocket,
    },
    md: {
        inner: recessedSurfaceClassNames.radius.innerMd,
        pocket: recessedSurfaceClassNames.radius.pocketMd,
    },
};

const surfaceShadowByName: Record<RecessedSurfaceShadow, string | undefined> = {
    none: undefined,
    'orange-sm': 'var(--shadow-orange-sm)',
    sm: 'var(--shadow-sm)',
};

const hoverSurfaceClassNameByTone: Record<RecessedSurfaceTone, string> = {
    gray: 'group-hover/recessed-surface:surface-recessed-inset-gray',
    inherit: '',
    orange: 'group-hover/recessed-surface:surface-recessed-inset-orange',
    white: 'group-hover/recessed-surface:bg-white-200 group-hover/recessed-surface:text-gray-700',
};

const hoverAndFocusSurfaceClassNameByTone: Record<RecessedSurfaceTone, string> = {
    gray: 'group-hover/recessed-surface:surface-recessed-inset-gray group-focus-within/recessed-surface:surface-recessed-inset-gray',
    inherit: '',
    orange: 'group-hover/recessed-surface:surface-recessed-inset-orange group-focus-within/recessed-surface:surface-recessed-inset-orange',
    white: 'group-hover/recessed-surface:bg-white-200 group-hover/recessed-surface:text-gray-700 group-focus-within/recessed-surface:bg-white-200 group-focus-within/recessed-surface:text-gray-700',
};

const hoverSurfaceShadowClassNameByName: Record<RecessedSurfaceShadow, string | undefined> = {
    none: undefined,
    'orange-sm': 'group-hover/recessed-surface:[box-shadow:var(--shadow-orange-sm)]',
    sm: 'group-hover/recessed-surface:[box-shadow:var(--shadow-sm)]',
};

const hoverAndFocusSurfaceShadowClassNameByName: Record<RecessedSurfaceShadow, string | undefined> = {
    none: undefined,
    'orange-sm':
        'group-hover/recessed-surface:[box-shadow:var(--shadow-orange-sm)] group-focus-within/recessed-surface:[box-shadow:var(--shadow-orange-sm)]',
    sm: 'group-hover/recessed-surface:[box-shadow:var(--shadow-sm)] group-focus-within/recessed-surface:[box-shadow:var(--shadow-sm)]',
};

const getPocketLayoutClassName = ({
    radius,
    shouldPreserveInactiveStructure,
    shouldRenderInactiveSurfaceShell,
}: {
    readonly radius: RecessedSurfaceRadius;
    readonly shouldPreserveInactiveStructure: boolean;
    readonly shouldRenderInactiveSurfaceShell: boolean;
}): string =>
    shouldPreserveInactiveStructure && !shouldRenderInactiveSurfaceShell
        ? 'overflow-visible rounded-none p-0'
        : clsx('overflow-hidden p-xxs', radiusClassNamesByRadius[radius].pocket);

const getInnerLayoutClassName = ({
    radius,
    shouldPreserveInactiveStructure,
    shouldRenderInactiveSurfaceShell,
}: {
    readonly radius: RecessedSurfaceRadius;
    readonly shouldPreserveInactiveStructure: boolean;
    readonly shouldRenderInactiveSurfaceShell: boolean;
}): string =>
    shouldPreserveInactiveStructure && !shouldRenderInactiveSurfaceShell
        ? 'overflow-visible rounded-none'
        : clsx('overflow-hidden', radiusClassNamesByRadius[radius].inner);

type RecessedSurfaceProps = HTMLAttributes<HTMLElement> & {
    readonly active: boolean;
    readonly as?: 'div' | 'span';
    readonly children: ReactNode;
    readonly hoverActive?: boolean;
    readonly hoverPocketTone?: RecessedSurfacePocketTone;
    readonly hoverSurfaceShadow?: RecessedSurfaceShadow;
    readonly hoverSurfaceTone?: RecessedSurfaceTone;
    readonly inactiveBehavior?: 'passthrough' | 'preserve-structure';
    readonly inactiveSurfaceShadow?: RecessedSurfaceShadow;
    readonly inactiveSurfaceTone?: RecessedSurfaceTone;
    readonly placement?: RecessedSurfacePlacement;
    readonly pocketTone?: RecessedSurfacePocketTone;
    readonly radius?: RecessedSurfaceRadius;
    readonly renderMode?: RecessedSurfaceRenderMode;
    readonly surfaceClassName?: string;
    readonly surfaceShadow?: RecessedSurfaceShadow;
    readonly surfaceStyle?: CSSProperties;
    readonly surfaceTone?: RecessedSurfaceTone;
    readonly transition?: RecessedSurfaceTransition;
};

type RecessedSurfaceComponent = 'div' | 'span';

type RecessedSurfaceOverlayProps = HTMLAttributes<HTMLElement> & {
    readonly active: boolean;
    readonly children: ReactNode;
    readonly Component: RecessedSurfaceComponent;
    readonly hoverActive: boolean;
    readonly hoverPocketTone: RecessedSurfacePocketTone;
    readonly hoverSurfaceShadow: RecessedSurfaceShadow;
    readonly hoverSurfaceTone: RecessedSurfaceTone;
    readonly inactiveSurfaceTone: RecessedSurfaceTone;
    readonly pocketTone: RecessedSurfacePocketTone;
    readonly radius: RecessedSurfaceRadius;
    readonly selectedSurfaceStyle: CSSProperties | undefined;
    readonly surfaceClassName: string | undefined;
    readonly surfaceTone: RecessedSurfaceTone;
    readonly transition: RecessedSurfaceTransition;
};

const RecessedSurfaceOverlay: FunctionComponent<RecessedSurfaceOverlayProps> = ({
    active,
    children,
    className,
    Component,
    hoverActive,
    hoverPocketTone,
    hoverSurfaceShadow,
    hoverSurfaceTone,
    inactiveSurfaceTone,
    pocketTone,
    radius,
    selectedSurfaceStyle,
    style,
    surfaceClassName,
    surfaceTone,
    transition,
    ...props
}) => (
    <Component
        className={clsx(
            'surface-recessed-overlay-pocket box-border w-full transition-[box-shadow] duration-200 ease',
            radiusClassNamesByRadius[radius].pocket,
            transition === 'none' && 'surface-recessed-overlay-pocket-no-transition transition-none',
            hoverActive && 'group/recessed-surface',
            active
                ? overlayPocketClassNameByTone[pocketTone]
                : hoverActive && hoverOverlayPocketClassNameByTone[hoverPocketTone],
            Component === 'span' && 'inline-flex',
            className,
        )}
        style={style}
        {...props}
    >
        <Component
            className={clsx(
                'relative box-border w-full overflow-hidden transition-[background-color,box-shadow] duration-200 ease',
                transition === 'none' && 'transition-none',
                Component === 'span' && 'inline-flex',
                radiusClassNamesByRadius[radius].inner,
                surfaceClassNameByTone[active ? surfaceTone : inactiveSurfaceTone],
                active && surfaceClassName,
                hoverActive && !active && hoverAndFocusSurfaceClassNameByTone[hoverSurfaceTone],
                hoverActive && !active && hoverAndFocusSurfaceShadowClassNameByName[hoverSurfaceShadow],
            )}
            style={selectedSurfaceStyle}
        >
            {children}
        </Component>
    </Component>
);

export const RecessedSurface: FunctionComponent<RecessedSurfaceProps> = ({
    active,
    as: Component = 'div',
    children,
    className,
    hoverActive = false,
    hoverPocketTone = 'neutral',
    hoverSurfaceShadow = 'none',
    hoverSurfaceTone = 'inherit',
    inactiveBehavior = 'passthrough',
    inactiveSurfaceShadow = 'none',
    inactiveSurfaceTone = 'inherit',
    placement = 'outside',
    pocketTone = 'neutral',
    radius = 'default',
    renderMode = 'layout',
    style,
    surfaceClassName,
    surfaceShadow = 'none',
    surfaceStyle,
    surfaceTone = 'inherit',
    transition = 'default',
    ...props
}) => {
    const shouldPreserveInactiveStructure =
        !active && placement === 'inside' && inactiveBehavior === 'preserve-structure';
    const shouldRenderInactiveSurfaceShell = shouldPreserveInactiveStructure && hoverActive;

    if (!active && placement === 'inside' && !shouldPreserveInactiveStructure) {
        return <>{children}</>;
    }

    const pocketStyle: CSSProperties = style ?? {};
    const selectedSurfaceShadow = surfaceShadowByName[active ? surfaceShadow : inactiveSurfaceShadow];
    const selectedSurfaceStyle: CSSProperties | undefined =
        selectedSurfaceShadow === undefined ? surfaceStyle : { boxShadow: selectedSurfaceShadow, ...surfaceStyle };

    if (renderMode === 'overlay') {
        return (
            <RecessedSurfaceOverlay
                active={active}
                className={className}
                Component={Component}
                hoverActive={hoverActive}
                hoverPocketTone={hoverPocketTone}
                hoverSurfaceShadow={hoverSurfaceShadow}
                hoverSurfaceTone={hoverSurfaceTone}
                inactiveSurfaceTone={inactiveSurfaceTone}
                pocketTone={pocketTone}
                radius={radius}
                selectedSurfaceStyle={selectedSurfaceStyle}
                style={style}
                surfaceClassName={surfaceClassName}
                surfaceTone={surfaceTone}
                transition={transition}
                {...props}
            >
                {children}
            </RecessedSurfaceOverlay>
        );
    }

    return (
        <Component
            className={clsx(
                'box-border w-full transition-[background-color,box-shadow] duration-200 ease',
                transition === 'none' && 'transition-none',
                hoverActive && 'group/recessed-surface',
                getPocketLayoutClassName({ radius, shouldPreserveInactiveStructure, shouldRenderInactiveSurfaceShell }),
                Component === 'span' && 'inline-flex',
                active ? pocketClassNameByTone[pocketTone] : 'bg-gray-200',
                shouldPreserveInactiveStructure && 'bg-transparent',
                hoverActive && !active && hoverPocketClassNameByTone[hoverPocketTone],
                placement === 'inside' && 'h-full',
                className,
            )}
            style={pocketStyle}
            {...props}
        >
            <Component
                className={clsx(
                    'box-border w-full transition-[background-color,box-shadow] duration-200 ease',
                    transition === 'none' && 'transition-none',
                    getInnerLayoutClassName({
                        radius,
                        shouldPreserveInactiveStructure,
                        shouldRenderInactiveSurfaceShell,
                    }),
                    Component === 'span' && 'inline-flex',
                    surfaceClassNameByTone[active ? surfaceTone : inactiveSurfaceTone],
                    hoverActive && !active && hoverSurfaceClassNameByTone[hoverSurfaceTone],
                    hoverActive && !active && hoverSurfaceShadowClassNameByName[hoverSurfaceShadow],
                    placement === 'inside' && 'h-full',
                    (!shouldPreserveInactiveStructure || shouldRenderInactiveSurfaceShell) && surfaceClassName,
                )}
                style={selectedSurfaceStyle}
            >
                {children}
            </Component>
        </Component>
    );
};
